import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SidebarMenuSkeleton } from "@volca/ui/components/ui/sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { SidebarSection } from "./nav-items";
import { SectionStatus } from "./section-status";
import { SIDEBAR_ICON_CURRENT } from "./sidebar-row";
import { rowShowsActive, useRowExpanded } from "./use-row-expanded";

const rowClass = (isActive: boolean) =>
  cn(
    "relative flex h-8 w-full items-center gap-2 rounded-lg px-2 text-start hover:bg-neutral-subtle-hovered",
    {
      "text-accent-orange before:absolute before:top-1/2 before:start-0 before:h-4 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-accent-orange-bold before:content-['']":
        isActive,
    },
  );

const controlClass =
  "grid size-6 place-items-center rounded-md text-subtle outline-hidden motion-safe:transition-[background-color,color,opacity] motion-safe:duration-150 motion-safe:ease-[var(--sidebar-ease-out)] hover:bg-neutral-subtle-hovered hover:text-ink focus-visible:ring-2 focus-visible:ring-focus";

const RowLink = ({
  row,
  active,
  onNavigate,
  className,
  iconClassName,
}: {
  row: SidebarSection;
  active: boolean;
  onNavigate?: () => void;
  className?: string;
  iconClassName?: string;
}) => (
  <Link
    to={row.to}
    onClick={onNavigate}
    aria-current={row.isActive ? "page" : undefined}
    className={cn(rowClass(active), className)}
  >
    {row.icon && (
      <row.icon
        weight={active ? "duotone" : "regular"}
        className={cn(
          "size-4 shrink-0",
          { [SIDEBAR_ICON_CURRENT]: active },
          iconClassName,
        )}
      />
    )}
    <Text variant="body" truncate render={<span />} className="min-w-0 flex-1">
      {row.label}
    </Text>
    <SectionStatus section={row} />
  </Link>
);

/**
 * A row that holds others, such as an agent and its pages. The row opens the
 * agent. On hover its icon turns into the chevron, which shows or hides the
 * pages, loading them first when they are not in yet. Its action, the
 * agent's Settings, is the control at the row's end.
 */
const TreeRow = ({
  row,
  onNavigate,
}: {
  row: SidebarSection;
  onNavigate?: () => void;
}) => {
  const { open, children } = useRowExpanded(row);
  const shown = open && children.length > 0;
  // Pages that arrive after the row (an agent's, once they load) mount folded
  // and unfold on the next frame; ones there from the start show as they are.
  const hasPages = children.length > 0;
  const [unfolded, setUnfolded] = useState(hasPages);
  useEffect(() => {
    if (!hasPages || unfolded) return;
    const frame = requestAnimationFrame(() => setUnfolded(true));
    return () => cancelAnimationFrame(frame);
  }, [hasPages, unfolded]);
  // Loaded with nothing under it (a workbook-only run): no chevron to offer.
  const expandable = !row.children || row.children.length > 0;
  const toggle = () => row.onExpandedChange?.(!open);
  return (
    <li>
      <div
        className="group/row relative"
        onPointerEnter={row.onIntent}
        onFocus={row.onIntent}
      >
        <RowLink
          row={row}
          active={rowShowsActive(row, shown)}
          onNavigate={onNavigate}
          className={cn({
            "group-hover/row:pe-8 group-has-[:focus-visible]/row:pe-8":
              row.action && !row.action.isActive,
            "pe-8": row.action?.isActive,
          })}
          iconClassName={cn({
            "motion-safe:transition-opacity motion-safe:duration-150 motion-safe:ease-[var(--sidebar-ease-out)] group-hover/row:opacity-0 group-has-[[data-row-toggle]:focus-visible]/row:opacity-0":
              expandable,
          })}
        />
        {expandable && (
          <button
            type="button"
            data-row-toggle
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "Show"} ${row.label} pages`}
            onClick={toggle}
            className={cn(
              controlClass,
              "absolute start-1 top-1 opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100",
            )}
          >
            <CaretRightIcon
              aria-hidden
              className={cn(
                "size-3.5 motion-safe:transition-transform motion-safe:duration-150 motion-safe:ease-[var(--sidebar-ease-out)]",
                { "rotate-90": open },
              )}
            />
          </button>
        )}
        {row.action && (
          <Link
            to={row.action.to}
            onClick={onNavigate}
            aria-label={`${row.label} ${row.action.label}`}
            aria-current={row.action.isActive ? "page" : undefined}
            className={cn(controlClass, "absolute end-1 top-1", {
              "opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100":
                !row.action.isActive,
              "text-accent-orange hover:text-accent-orange":
                row.action.isActive,
            })}
          >
            <row.action.icon
              aria-hidden
              weight={row.action.isActive ? "duotone" : "regular"}
              className="size-4"
            />
          </Link>
        )}
      </div>
      {children.length > 0 && (
        <div
          data-open={(shown && unfolded) || undefined}
          inert={!shown || undefined}
          className="nav-tree"
        >
          <div>
            <Stack
              gap={0.5}
              render={<ul />}
              className="ms-4 mt-0.5 border-s border-line ps-1.5"
            >
              {children.map((child) => (
                <PanelRow
                  key={child.to}
                  // Folding away, the row above marks the page, not these.
                  row={
                    shown || !child.isActive
                      ? child
                      : { ...child, isActive: false }
                  }
                  onNavigate={onNavigate}
                />
              ))}
            </Stack>
          </div>
        </div>
      )}
    </li>
  );
};

export const PanelRow = ({
  row,
  onNavigate,
}: {
  row: SidebarSection;
  onNavigate?: () => void;
}) => {
  if (row.pending)
    return (
      <li aria-hidden>
        <SidebarMenuSkeleton showIcon />
      </li>
    );
  if (row.expanded !== undefined)
    return <TreeRow row={row} onNavigate={onNavigate} />;
  return (
    <li>
      <RowLink row={row} active={row.isActive} onNavigate={onNavigate} />
    </li>
  );
};
